/**
 * Client-side image compression.
 * Loads the file into a canvas, resizes so the longest edge is `maxWidth`px,
 * and re-encodes as JPEG at the given quality. Non-image files are returned as-is.
 *
 * Typical results: a 4MB phone photo compresses to ~200–400KB while remaining
 * more than enough resolution for site updates on Right Hand.
 */
export async function compressImage(file, { maxWidth = 1600, quality = 0.7 } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // Skip vector + animated formats — rasterising them via <canvas> would
  // destroy the vector or drop frames.
  if (file.type === "image/svg+xml" || file.type === "image/gif") return file;
  // Very small files aren't worth reprocessing.
  if (file.size < 200_000) return file;

  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("Could not decode image"));
    el.src = dataUrl;
  });

  const scale = Math.min(1, maxWidth / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
  );
  if (!blob) return file;

  // Preserve the original filename with a .jpg extension.
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}
